import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listQaSurfaceVerdicts,
  setQaSurfaceVerdict,
  clearQaSurfaceVerdict,
  type QaSurfaceVerdict,
  type QaSurfaceVerdictRow,
} from '@pc/db';
import { QA_CATALOG, type QaCatalogEntry } from './qaCatalog.generated';
import { QA_ROUTE_MAP } from './qaRouteMap';

const VERDICTS_QUERY_KEY = ['devMode', 'qaSurfaceVerdicts'] as const;

export interface QaSurface extends QaCatalogEntry {
  /** Current in-app verdict, if any. */
  verdict: QaSurfaceVerdict | null;
  verdictNote: string | null;
  verdictAt: string | null;
  /** Route to deep-link to, or null. */
  route: string | null;
  /** How-to-verify hint (frontmatter wins, else route map). */
  hint: string | null;
  /**
   * True when the markdown status regressed (not green) AFTER it was marked
   * verified in-app — i.e. the verdict is stale and should be re-checked.
   */
  needsReverify: boolean;
}

/** A surface is "pending verification" if it isn't green and isn't archived. */
function isPending(entry: QaCatalogEntry): boolean {
  return entry.status !== 'green' && entry.status !== 'archived';
}

/**
 * Loads the QA-surface verdicts and merges them with the build-time catalog.
 * Returns the full merged list plus the pending subset and counts the Dev Mode
 * banner/hub render. Verdicts are platform-admin-gated server-side; non-admins
 * get an empty verdict list (so everything shows as unverified, harmlessly).
 */
export function useQaSurfaces() {
  const verdictsQ = useQuery({
    queryKey: VERDICTS_QUERY_KEY,
    queryFn: listQaSurfaceVerdicts,
    staleTime: 30_000,
  });

  const verdictBySlug = useMemo(() => {
    const map = new Map<string, QaSurfaceVerdictRow>();
    for (const v of verdictsQ.data ?? []) map.set(v.checklist_slug, v);
    return map;
  }, [verdictsQ.data]);

  const surfaces = useMemo<QaSurface[]>(() => {
    return QA_CATALOG.map((entry) => {
      const v = verdictBySlug.get(entry.slug) ?? null;
      const mapping = QA_ROUTE_MAP[entry.slug];
      const route = entry.verifyRoute ?? mapping?.route ?? null;
      const hint = entry.verifyHint ?? mapping?.hint ?? null;
      // Re-verify when we marked it verified in-app but the markdown checklist
      // was reset (source changed) AFTER that verdict — the verdict is stale.
      const needsReverify = Boolean(
        v?.verdict === 'verified' &&
          v.set_at &&
          entry.lastResetAt &&
          new Date(entry.lastResetAt).getTime() > new Date(v.set_at).getTime(),
      );
      return {
        ...entry,
        verdict: v?.verdict ?? null,
        verdictNote: v?.note ?? null,
        verdictAt: v?.set_at ?? null,
        route,
        hint,
        needsReverify,
      };
    });
  }, [verdictBySlug]);

  const pending = useMemo(() => surfaces.filter((s) => isPending(s)), [surfaces]);

  // "Left to verify" = pending surfaces without a verdict yet.
  const remaining = useMemo(
    () => pending.filter((s) => s.verdict === null).length,
    [pending],
  );

  return {
    isLoading: verdictsQ.isLoading,
    isError: verdictsQ.isError,
    surfaces,
    pending,
    remaining,
    total: pending.length,
  };
}

/** Mutations for setting / clearing a surface verdict, with cache refresh. */
export function useQaSurfaceMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: VERDICTS_QUERY_KEY });

  const setVerdict = useMutation({
    mutationFn: (input: {
      slug: string;
      kind: QaCatalogEntry['kind'];
      verdict: QaSurfaceVerdict;
      note?: string | null;
    }) => setQaSurfaceVerdict(input),
    onSuccess: invalidate,
  });

  const clearVerdict = useMutation({
    mutationFn: (slug: string) => clearQaSurfaceVerdict(slug),
    onSuccess: invalidate,
  });

  return { setVerdict, clearVerdict };
}
